/**
 * Clerk (Layer 2).
 *
 * Loaded from Clerk's own CDN via a script tag in index.html rather than
 * bundled. Two reasons: the npm package pulls in Clerk's Web3 sign-in support
 * (the whole Solana/React-Native tree, ~20 advisories) that this app never
 * touches, and the CDN build is the one Clerk serves for this instance.
 *
 * The trade is that `window.Clerk` arrives asynchronously, so everything here
 * waits on `ready()` before touching it.
 */

export interface ClerkSession {
  id: string;
  getToken(): Promise<string | null>;
}

interface ClerkGlobal {
  loaded: boolean;
  session: ClerkSession | null;
  load(options?: Record<string, unknown>): Promise<void>;
  signOut(
    callbackOrOptions?: (() => void) | { redirectUrl?: string },
  ): Promise<void>;
  mountSignIn(
    node: HTMLElement,
    props?: {
      routing?: "path" | "hash" | "virtual";
      forceRedirectUrl?: string;
      fallbackRedirectUrl?: string;
      signUpUrl?: string;
    },
  ): void;
  unmountSignIn(node: HTMLElement): void;
  addListener(fn: (payload: { session: ClerkSession | null }) => void): () => void;
}

declare global {
  interface Window {
    Clerk?: ClerkGlobal;
  }
}

let readyPromise: Promise<ClerkGlobal> | null = null;

/** From page start to a usable Clerk: the script arriving and load() settling. */
const LOAD_DEADLINE_MS = 20_000;

/** Resolve once the CDN script has landed and Clerk has finished loading. */
export function ready(): Promise<ClerkGlobal> {
  if (readyPromise) return readyPromise;

  readyPromise = new Promise<ClerkGlobal>((resolve, reject) => {
    const start = Date.now();

    const attempt = async () => {
      const clerk = window.Clerk;
      if (clerk) {
        try {
          // ⚠ The deadline covers load() too, not just the script arriving. A
          // load() that never settles (a blocked API call, a privacy feature
          // stalling the request) otherwise leaves a bare spinner forever,
          // which reads to the user as a blank page.
          if (!clerk.loaded) {
            await Promise.race([
              clerk.load(),
              new Promise<never>((_, fail) =>
                setTimeout(
                  () => fail(new Error("Clerk did not finish loading")),
                  Math.max(0, LOAD_DEADLINE_MS - (Date.now() - start)),
                ),
              ),
            ]);
          }
          resolve(clerk);
        } catch (err) {
          reject(err instanceof Error ? err : new Error(String(err)));
        }
        return;
      }
      // A blocked or failed CDN load must surface as an error rather than a
      // spinner that never resolves.
      if (Date.now() - start > LOAD_DEADLINE_MS) {
        reject(new Error("Clerk failed to load"));
        return;
      }
      setTimeout(() => void attempt(), 50);
    };

    void attempt();
  });

  return readyPromise;
}

/**
 * The Bearer token for an API call, or null when there is no active session.
 *
 * ⚠ Called with no arguments on purpose. `getToken({ template })` is for
 * third-party services (Supabase, Hasura, Firebase); requesting an undefined
 * template fails with a 404 "No JWT template exists". The default session token
 * already carries `sub` and the instance-level `email` claim, which is all the
 * auth pattern needs.
 */
export async function getToken(): Promise<string | null> {
  const clerk = window.Clerk;
  if (!clerk?.loaded || !clerk.session) return null;
  try {
    return await clerk.session.getToken();
  } catch {
    return null;
  }
}

export async function signOut(): Promise<void> {
  const clerk = window.Clerk;
  if (!clerk?.loaded) return;
  /**
   * ⚠ The empty callback is load-bearing: it replaces clerk-js's navigation
   * step, so signing out changes nothing about the page. With no arguments
   * clerk-js navigates to the instance's `after_sign_out_all_url` — the
   * Account Portal at `accounts.leychoon.app`, another app entirely — and any
   * `redirectUrl` still forces a full reload, which turns the automatic
   * sign-out on a Layer-1 refusal into a reload loop. In-place, the session
   * listener fires and the app renders its own sign-in.
   */
  await clerk.signOut(() => {}).catch(() => {
    // Already signed out, or the network is down — either way the app treats
    // the absent session as signed out.
  });
}

export function hasSession(): boolean {
  return Boolean(window.Clerk?.loaded && window.Clerk.session);
}
